import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from 'contexts/AuthContext';

// ==============================|| ROLE-BASED ACCESS CONTROL ||============================== //

const SUPERVISOR_EQUIVALENTS = new Set(['supervisor', 'comercial']);

export const usePermissions = () => {
  const { user } = useAuth();

  const hasRole = (requiredRole) => {
    if (!requiredRole) return true;
    if (!user) return false;
    if (user.role === 'admin') return true;
    const normalized = String(requiredRole || '').trim().toLowerCase();
    if (!normalized) return true;
    if (normalized === 'supervisor') {
      return SUPERVISOR_EQUIVALENTS.has(user.role);
    }
    return user.role === normalized;
  };

  const canAccess = (item) => hasRole(item.requiredRole);

  const isAdmin = () => user?.role === 'admin';
  const isSupervisor = () => SUPERVISOR_EQUIVALENTS.has(user?.role);
  const isOperario = () => user?.role === 'operario';
  const isContabilidad = () => user?.role === 'contabilidad';
  const isComercial = () => user?.role === 'comercial';

  return {
    hasRole,
    canAccess,
    isAdmin,
    isSupervisor,
    isOperario,
    isContabilidad,
    isComercial,
    userRole: user?.role
  };
};

const OPERARIO_RESTRICTED_MENU_IDS = new Set([
  'oportunidades',
  'visitas',
  'pendiente-boletin',
  'terminados',
  'rechazadas'
]);

const filterMenuItems = (items = [], isOperario) => {
  if (!Array.isArray(items)) return [];
  return items.reduce((acc, item) => {
    const nextItem = { ...item };
    if (Array.isArray(nextItem.children)) {
      nextItem.children = filterMenuItems(nextItem.children, isOperario);
    }
    if (isOperario && nextItem.id && OPERARIO_RESTRICTED_MENU_IDS.has(nextItem.id)) {
      return acc;
    }
    acc.push(nextItem);
    return acc;
  }, []);
};

export const filterMenuByPermissions = (menuItems, userRole) => {
  if (!menuItems || !Array.isArray(menuItems)) return [];
  const normalizedRole = String(userRole || '').trim().toLowerCase();
  const isOperario = normalizedRole === 'operario';
  return filterMenuItems(menuItems, isOperario);
};

export const withRoleProtection = (WrappedComponent, requiredRole = null) => {
  return function ProtectedComponent(props) {
    const { hasRole } = usePermissions();
    const { user } = useAuth();

    if (!requiredRole) {
      return <WrappedComponent {...props} />;
    }

    const hasRequiredRole = Array.isArray(requiredRole)
      ? requiredRole.some((role) => hasRole(role))
      : hasRole(requiredRole);

    if (!hasRequiredRole) {
      return <Navigate to="/dashboard/default" replace state={{ fromRestricted: true, requiredRole, currentRole: user?.role }} />;
    }

    return <WrappedComponent {...props} />;
  };
};

export default usePermissions;
